"use client";

import React from "react";
import { BodyText } from "src/components/KinsaTypography";
import type { BillingPeriod } from "../hooks/use-paywall-state";

interface BillingToggleProps {
  billingPeriod: BillingPeriod;
  onBillingPeriodChange: (period: BillingPeriod) => void;
}

/**
 * Segmented control for Yearly/Monthly billing period selection
 * Matches Figma design with dark background and rounded-full container
 * Includes smooth transition animation for the teal selector
 * Yearly is now first as per requirements
 */
export function BillingToggle({
  billingPeriod,
  onBillingPeriodChange,
}: BillingToggleProps) {
  const isYearly = billingPeriod === "yearly";

  return (
    <div className="bg-[#001720] flex items-center rounded-[1000px] w-full relative p-0.5">
      {/* Sliding indicator background */}
      <div
        className={`absolute top-0.5 bottom-0.5 rounded-[10000px] transition-all duration-300 ease-in-out ${
          isYearly ? "left-0.5 right-1/2" : "left-1/2 right-0.5"
        }`}
        style={{
          background: "linear-gradient(100deg, #01667F 0%, #007F9D 100%)",
        }}
        aria-hidden="true"
      />

      {/* Buttons - Yearly first */}
      <button
        type="button"
        onClick={() => onBillingPeriodChange("yearly")}
        className="basis-0 flex flex-col grow items-center justify-center min-h-px min-w-px overflow-clip px-0 py-[8px] relative rounded-[10000px] shrink-0 z-10 transition-colors duration-300"
        aria-pressed={isYearly}
      >
        <BodyText className="text-white relative z-10">Yearly</BodyText>
      </button>
      <button
        type="button"
        onClick={() => onBillingPeriodChange("monthly")}
        className="basis-0 flex flex-col grow items-center justify-center min-h-px min-w-px overflow-clip px-0 py-[8px] relative rounded-[10000px] shrink-0 z-10 transition-colors duration-300"
        aria-pressed={!isYearly}
      >
        <BodyText className="text-white relative z-10">Monthly</BodyText>
      </button>
    </div>
  );
}
